import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ClockIcon, MapPinIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { eventsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
  AttendanceStatus,
  Event,
  EventComment,
  EventResponse,
  EventType,
} from '../../types/event';
import type { Match } from '../../types/match';
import { MatchIncidentType, getMatchOutcome } from '../../types/match';
import { useClubSettings } from '../../contexts/ClubSettingsContext';
import { displayName } from '../../utils/brand';
import type { FamilyEvent } from '../../types/family';
import { familyAPI } from '../../services/api';
import { BackHeader, Group, LoadingRows, Row, SectionLabel } from '../../components/family/ui';
import EventResponsePanel from '../../components/events/EventResponsePanel';
import { dayLabel, eventTypeName, isoDate, mapsUrl, shortNames, timeOf } from '../../components/family/format';
import TravelSection from '../../components/travel/TravelSection';
import EventBoardPosts from '../../components/board/EventBoardPosts';
import TrainingFeedbackSection from '../../components/events/TrainingFeedbackSection';
import CommentSection from '../../components/events/CommentSection';

const card = 'rounded-2xl bg-white p-4 ring-1 ring-black/5';

/**
 * Egy esemény a családi nézetben, teljes oldalként (a push értesítések is ide
 * visznek): mikor, hol, legfelül a gyerekenkénti visszajelzés (jön-e, hogyan
 * jut el, kikérő), ki jön még, a megszervezett utazás, meccsnél az eredmény,
 * hozzászólások. A felállás és a pályafoglalás a stábra tartozik, itt nincs.
 */
const FamilyEventPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const eventId = Number(id);
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const { settings } = useClubSettings();

  const [event, setEvent] = useState<Event | null>(null);
  const [summary, setSummary] = useState<FamilyEvent | null>(null);
  const [responses, setResponses] = useState<EventResponse[]>([]);
  const [comments, setComments] = useState<EventComment[]>([]);
  const [match, setMatch] = useState<Match | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showWho, setShowWho] = useState(false);
  const [travelRefresh, setTravelRefresh] = useState(0);
  const loadedFor = useRef<number | null>(null);

  usePageTitle(event?.title ?? 'Esemény');

  const loadResponses = useCallback(async () => {
    const [r, c] = await Promise.all([eventsAPI.getResponses(eventId), eventsAPI.getComments(eventId)]);
    setResponses(r);
    setComments(c);
  }, [eventId]);

  useEffect(() => {
    if (!eventId || loadedFor.current === eventId) return;
    loadedFor.current = eventId;
    (async () => {
      try {
        const loaded = await eventsAPI.getById(eventId);
        setEvent(loaded);
        // A visszajelzés gyerekenként a családi programból jön
        const day = isoDate(new Date(loaded.startTime));
        const agenda = await familyAPI.getAgenda(day, day);
        const mine = agenda.events.find((e) => e.id === eventId) ?? null;
        setSummary(mine);
        await loadResponses();
        if (loaded.type === EventType.Match) {
          eventsAPI.getMatch(eventId).then(setMatch).catch(() => setMatch(null));
        }
      } catch (err: any) {
        if (err.response?.status === 404 || err.response?.status === 403) setNotFound(true);
        else showToast('Az esemény betöltése nem sikerült.', 'error');
      }
    })();
  }, [eventId, loadResponses, showToast]);

  const onResponse = (playerId: number, status: AttendanceStatus) => {
    setSummary((prev) =>
      prev && {
        ...prev,
        myPlayers: prev.myPlayers.map((p) => (p.playerId === playerId ? { ...p, status } : p)),
      }
    );
    // A létszám és a névsor a szerverről frissül (a panel már a visszaigazolt állapotot adja)
    loadResponses().catch(() => undefined);
  };

  const coming = useMemo(
    () => responses.filter((r) => r.status === AttendanceStatus.Attending),
    [responses]
  );
  const notComing = useMemo(
    () => responses.filter((r) => r.status === AttendanceStatus.NotAttending),
    [responses]
  );

  if (notFound) {
    return (
      <div>
        <BackHeader backLabel="Program" back="/program" />
        <p className={`${card} text-center text-[15px] text-gray-600`}>Ez az esemény nem található, vagy már törölték.</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div>
        <BackHeader backLabel="Program" back="/program" />
        <LoadingRows rows={3} />
      </div>
    );
  }

  const now = new Date();
  const started = new Date(event.startTime) <= now;
  const ended = new Date(event.endTime) <= now;
  const cancelled = summary?.isCancelled ?? false;
  const isMatch = event.type === EventType.Match;
  const place = event.venue?.name || event.location;
  const map = mapsUrl(place, event.venue?.address);
  const myPlayers = summary?.myPlayers ?? [];
  const names = shortNames(myPlayers);
  const canRespond = event.responsesEnabled && !started && !cancelled && myPlayers.length > 0;
  const hasScore = isMatch && match?.homeScore != null && match?.awayScore != null;
  const goals = match?.incidents.filter((i) => i.type === MatchIncidentType.Goal) ?? [];
  // A hazai csapat áll elöl, ahogy a focieredményeknél szokás; a mi nevünk a klubé
  const ourName = displayName(settings?.clubName);
  const opponentName = match?.opponentName || 'Ellenfél';
  const homeName = match?.isHomeMatch === false ? opponentName : match?.isHomeMatch ? ourName : 'Hazai';
  const awayName = match?.isHomeMatch === false ? ourName : match?.isHomeMatch ? opponentName : 'Vendég';
  const outcome = match ? getMatchOutcome(match) : null;
  const outcomeLabel = outcome === 'win' ? 'Győzelem' : outcome === 'loss' ? 'Vereség' : outcome === 'draw' ? 'Döntetlen' : null;

  return (
    <div>
      <BackHeader backLabel="Program" back="/program" />

      <p className="text-[13px] font-semibold uppercase tracking-wide text-primary-600">
        {eventTypeName[event.type]}
        {isMatch && match?.isFutsal && ' · futsal'}
        {event.ageGroup && ` · ${event.ageGroup}`}
      </p>
      <h1 className={`mt-1 text-[26px] font-bold leading-tight text-gray-900 ${cancelled ? 'line-through' : ''}`}>
        {isMatch && match?.opponentName ? match.opponentName : event.title}
      </h1>
      {isMatch && match?.isHomeMatch != null && (
        <p className="mt-0.5 text-[15px] text-gray-500">{match.isHomeMatch ? 'Hazai mérkőzés' : 'Idegenben'}</p>
      )}

      {cancelled && (
        <p className="mt-4 rounded-2xl bg-red-50 p-4 text-[15px] font-medium text-red-800 ring-1 ring-red-100">
          Ez az esemény elmarad.
        </p>
      )}

      {hasScore && (
        <div className={`${card} mt-4 text-center`}>
          <p className="text-[13px] font-semibold uppercase tracking-wide text-gray-500">
            {outcomeLabel ?? 'Végeredmény'}
          </p>
          <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <span className="text-right text-[15px] font-medium leading-tight text-gray-700">{homeName}</span>
            <span className="text-[40px] font-bold tabular-nums leading-none text-gray-900">
              {match!.homeScore}–{match!.awayScore}
            </span>
            <span className="text-left text-[15px] font-medium leading-tight text-gray-700">{awayName}</span>
          </div>
          {goals.length > 0 && (
            <p className="mt-3 text-[14px] text-gray-600">
              Gól: {goals.map((g) => `${g.playerName ?? '?'}${g.minute ? ` (${g.minute}')` : ''}`).join(', ')}
            </p>
          )}
        </div>
      )}

      <Group className="mt-4">
        <Row
          icon={ClockIcon}
          title={dayLabel(event.startTime)}
          subtitle={`${timeOf(event.startTime)}–${timeOf(event.endTime)}`}
        />
        {place && (
          <Row
            icon={MapPinIcon}
            href={map ?? undefined}
            title={place}
            subtitle={event.venue?.address ?? (map ? 'Útvonal a térképen' : undefined)}
            chevron={Boolean(map)}
          />
        )}
      </Group>

      {canRespond && (
        <>
          <SectionLabel>{myPlayers.length === 1 ? 'Jön?' : 'Kik jönnek?'}</SectionLabel>
          <div className={card}>
            <EventResponsePanel
              eventId={event.id}
              excuseLetterEnabled={event.excuseLetterEnabled}
              onStatusChange={onResponse}
              travelRefreshKey={travelRefresh}
              onTravelChanged={() => setTravelRefresh((n) => n + 1)}
            />
          </div>
        </>
      )}

      {!canRespond && !ended && myPlayers.length > 0 && event.responsesEnabled && !cancelled && (
        <p className="mt-3 px-1 text-[14px] text-gray-500">
          {myPlayers
            .map((p) =>
              `${names[p.playerId]}: ${
                !p.inSquad
                  ? 'nincs a keretben'
                  : p.status === AttendanceStatus.Attending
                    ? 'jön'
                    : p.status === AttendanceStatus.NotAttending
                      ? 'nem jön'
                      : 'nem jelzett'
              }`
            )
            .join(' · ')}
        </p>
      )}

      {event.description && (
        <p className={`${card} mt-3 whitespace-pre-line text-[15px] text-gray-800`}>{event.description}</p>
      )}

      {event.responsesEnabled && !cancelled && !ended && (
        <>
          <SectionLabel>Ki jön?</SectionLabel>
          <Group>
            <Row
              icon={UserGroupIcon}
              onClick={() => setShowWho((v) => !v)}
              title={`${coming.length} jön${notComing.length ? ` · ${notComing.length} nem jön` : ''}`}
              subtitle={showWho ? undefined : 'Koppints a névsorért'}
              chevron={false}
            />
            {showWho && (
              <div className="px-4 py-3 text-[15px]">
                {coming.length > 0 && (
                  <p className="text-gray-900">
                    <span className="font-semibold text-green-700">Jön: </span>
                    {coming.map((r) => r.playerName ?? r.userName).join(', ')}
                  </p>
                )}
                {notComing.length > 0 && (
                  <p className="mt-2 text-gray-900">
                    <span className="font-semibold text-red-700">Nem jön: </span>
                    {notComing.map((r) => r.playerName ?? r.userName).join(', ')}
                  </p>
                )}
                {coming.length === 0 && notComing.length === 0 && <p className="text-gray-500">Még senki nem jelzett.</p>}
              </div>
            )}
          </Group>
        </>
      )}

      {!cancelled && !ended && (
        <TravelSection
          eventId={event.id}
          refreshKey={travelRefresh}
          className={card}
          compact
          heading={<SectionLabel>Utazás</SectionLabel>}
          respondElsewhere={canRespond}
          onChanged={() => setTravelRefresh((n) => n + 1)}
        />
      )}

      {event.type === EventType.Training && (
        <div className="mt-6">
          <TrainingFeedbackSection
            eventId={event.id}
            startTime={event.startTime}
            staffView={false}
            autoFocus={searchParams.get('feedback') === '1'}
          />
        </div>
      )}

      <EventBoardPosts eventId={event.id} className={card} heading={<SectionLabel>Üzenőfal</SectionLabel>} />

      <div className={`${card} mt-7`}>
        <CommentSection
          comments={comments}
          onAddComment={async (content) => {
            await eventsAPI.createComment(event.id, { content });
            await loadResponses();
          }}
          onUpdateComment={async (commentId, content) => {
            await eventsAPI.updateComment(event.id, commentId, { content });
            await loadResponses();
          }}
          onDeleteComment={async (commentId) => {
            await eventsAPI.deleteComment(event.id, commentId);
            await loadResponses();
          }}
        />
      </div>
    </div>
  );
};

export default FamilyEventPage;
